import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import type { ScheduledTask } from '../types/scheduler.types';
import { useGetScheduledTaskLogsQuery } from 'src/redux/operations/operations.endpoints';
import { SchedulerStatusBadge } from './scheduler-status-badge';

interface SchedulerRunHistoryProps {
  task: ScheduledTask | null;
}

function formatTriggeredBy(log: {
  trigger_type: string;
  triggered_by: { name: string | null; email: string | null } | null;
}): string {
  if (log.trigger_type === 'manual') {
    return log.triggered_by?.name ?? log.triggered_by?.email ?? '—';
  }
  return '—';
}

function RunErrorMessage({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = message.length > 160;
  const displayText = expanded || !isLong ? message : `${message.slice(0, 160)}…`;

  return (
    <div className="max-w-md text-sm text-red-700">
      <p className="whitespace-pre-wrap break-words">
        {displayText}
      </p>
      {isLong ? (
        <button
          type="button"
          className="mt-1 text-xs font-medium text-red-700 underline-offset-2 hover:underline"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}

export function SchedulerRunHistory({ task }: SchedulerRunHistoryProps) {
  const { data: logs, isLoading, isError } = useGetScheduledTaskLogsQuery(
    { taskId: task?.id ?? 0 },
    { skip: !task?.id }
  );

  if (!task) {
    return null;
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-gray-500"
        role="status"
      >
        <Loader2 className="size-4 animate-spin" />
        Loading run history…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700"
        role="alert"
      >
        Could not load run history for
        {' '}
        {task.name}
        .
      </div>
    );
  }

  if (!logs?.length) {
    return (
      <p className="text-sm text-gray-500">
        No runs recorded yet for
        {' '}
        {task.name}
        .
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-gray-400">
      <Table>
        <TableHeader>
          <TableRow className="border-gray-400 bg-gray-200 hover:bg-gray-200">
            <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
              Status
            </TableHead>
            <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
              Trigger
            </TableHead>
            <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
              Triggered by
            </TableHead>
            <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
              Started
            </TableHead>
            <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
              Finished
            </TableHead>
            <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
              Details
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => (
            <TableRow
              key={log.id}
              className="border-b border-gray-300 align-top"
            >
              <TableCell className="px-4 py-3">
                <SchedulerStatusBadge status={log.status} />
              </TableCell>
              <TableCell className="px-4 py-3 text-sm text-gray-700">
                {log.trigger_type === 'manual' ? (
                  <span className="inline-flex items-center rounded-full bg-[#EDEEF2] px-2 py-0.5 text-xs font-medium text-[#5E6278]">
                    Manual
                  </span>
                ) : null}
              </TableCell>
              <TableCell className="px-4 py-3 text-sm text-gray-700">
                {formatTriggeredBy(log)}
              </TableCell>
              <TableCell className="px-4 py-3 text-sm text-black">
                {log.started_at
                  ? formatDisplayDateTime(log.started_at)
                  : log.run_at
                    ? formatDisplayDateTime(log.run_at)
                    : '—'}
              </TableCell>
              <TableCell className="px-4 py-3 text-sm text-black">
                {log.finished_at ? formatDisplayDateTime(log.finished_at) : '—'}
              </TableCell>
              <TableCell className="px-4 py-3 text-sm text-gray-700">
                {log.status === 'failed' && log.error_message ? (
                  <RunErrorMessage message={log.error_message} />
                ) : (
                  <p className="max-w-md whitespace-pre-wrap break-words">
                    {log.output ?? '—'}
                  </p>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
